import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Pencil, Trash2, Save, X, Building, MapPin, Calendar, Clock, CheckCircle, AlertCircle, Download } from "lucide-react";

const API_URL = "https://nspo-backend-gray.vercel.app/api/jobs";

const ManageJobsTab = () => {
  const [jobs, setJobs] = useState([]);
  const [editingJob, setEditingJob] = useState(null);
  const [loading, setLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(null);
  const [downloading, setDownloading] = useState({});

  // دالة للحصول على التوكن من localStorage
  const getToken = () => {
    return localStorage.getItem("adminToken") || localStorage.getItem("token");
  };

  // 🟢 Fetch jobs - تم التعديل لترتيب الوظائف من الأحدث إلى الأقدم
  const fetchJobs = async () => {
    try {
      const token = getToken();
      if (!token) {
        toast.error("❌ يجب تسجيل الدخول أولاً");
        return;
      }

      const res = await fetch(API_URL, {
        headers: {
          "Authorization": `Bearer ${token}`
        }
      });
      
      if (!res.ok) throw new Error("فشل في تحميل الوظائف");
      
      const data = await res.json();
      
      // ترتيب الوظائف من الأحدث إلى الأقدم حسب تاريخ الإنشاء
      const sortedJobs = data.sort((a, b) => {
        const dateA = new Date(a.createdAt || a.startDate || 0);
        const dateB = new Date(b.createdAt || b.startDate || 0);
        return dateB - dateA; // الأحدث أولاً
      });
      
      setJobs(sortedJobs);
    } catch (err) {
      toast.error("❌ فشل في تحميل الوظائف");
    }
  };

  useEffect(() => {
    fetchJobs();
  }, []);

  // التحقق إذا كانت الوظيفة منتهية
  const isJobExpired = (endDate) => {
    return new Date(endDate) < new Date();
  };

  // التحقق إذا كانت الوظيفة على وشك الانتهاء (أقل من 3 أيام)
  const isJobExpiringSoon = (endDate) => {
    const threeDaysFromNow = new Date();
    threeDaysFromNow.setDate(threeDaysFromNow.getDate() + 3);
    return new Date(endDate) <= threeDaysFromNow && new Date(endDate) >= new Date();
  };

  // 🟠 Delete job
  const deleteJob = async (id, jobTitle) => {
    if (!window.confirm(`هل أنت متأكد من حذف الوظيفة "${jobTitle}"؟`)) return;

    setDeleteLoading(id);
    try {
      const token = getToken();
      if (!token) {
        toast.error("❌ يجب تسجيل الدخول أولاً");
        return;
      }

      const res = await fetch(`${API_URL}/${id}`, { 
        method: "DELETE",
        headers: {
          "Authorization": `Bearer ${token}`
        }
      });
      
      if (!res.ok) throw new Error("فشل في حذف الوظيفة");
      
      toast.success("🎉 تم الحذف بنجاح", {
        description: `تم حذف وظيفة "${jobTitle}"`,
        duration: 4000,
      });
      setJobs((prev) => prev.filter((j) => j._id !== id));
    } catch {
      toast.error("❌ فشل في الحذف", {
        description: "يرجى المحاولة مرة أخرى",
        duration: 4000,
      });
    } finally {
      setDeleteLoading(null);
    }
  };

  // 🟡 Edit job
  const handleEdit = (job) => {
    setEditingJob({
      ...job,
      startDate: job.startDate?.split("T")[0] || "",
      endDate: job.endDate?.split("T")[0] || ""
    });
  };

  const handleCancelEdit = () => {
    setEditingJob(null);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setEditingJob((prev) => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    if (!editingJob) return;

    const token = getToken();
    if (!token) {
      toast.error("❌ يجب تسجيل الدخول أولاً");
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/${editingJob._id}`, {
        method: "PUT",
        headers: { 
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify(editingJob),
      });

      if (!res.ok) throw new Error("فشل في تعديل الوظيفة");

      toast.success("✅ تم التعديل بنجاح", {
        description: `تم تحديث وظيفة "${editingJob.title}"`,
        duration: 4000,
      });
      setEditingJob(null);
      fetchJobs(); // إعادة تحميل القائمة بعد التعديل للحفاظ على الترتيب
    } catch {
      toast.error("❌ فشل في التعديل", {
        description: "يرجى المحاولة مرة أخرى",
        duration: 4000,
      });
    } finally {
      setLoading(false);
    }
  };

  // 📥 Download Excel function
  const downloadExcel = async (jobId, jobTitle) => {
    try {
      setDownloading(prev => ({ ...prev, [jobId]: true }));
      
      const token = getToken();
      if (!token) {
        toast.error("❌ يجب تسجيل الدخول أولاً");
        return;
      }

      const response = await fetch(`https://nspo-backend-gray.vercel.app/api/applicants/export-by-job/${jobId}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const blob = await response.blob();
        
        // إنشاء رابط تحميل
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.style.display = 'none';
        a.href = url;
        
        // اسم الملف
        const fileName = `applicants_${jobTitle.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.xlsx`;
        a.download = fileName;
        
        document.body.appendChild(a);
        a.click();
        
        // التنظيف
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        
        toast.success("✅ تم تحميل الملف بنجاح", {
          description: `تم تحميل قائمة المتقدمين للوظيفة "${jobTitle}"`,
          duration: 4000,
        });
      } else {
        const errorData = await response.json();
        throw new Error(errorData.message || 'فشل في تحميل الملف');
      }
    } catch (error) {
      console.error('Error downloading Excel:', error);
      toast.error("❌ فشل في تحميل الملف", {
        description: error.message,
        duration: 4000,
      });
    } finally {
      setDownloading(prev => ({ ...prev, [jobId]: false }));
    }
  };

  // تنسيق التاريخ
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('ar-EG');
  };

  // دالة إضافية لعرض تاريخ الإنشاء إذا كان موجوداً
  const getCreationDate = (job) => {
    if (job.createdAt) {
      return formatDate(job.createdAt);
    }
    return "غير محدد";
  };

  return (
    <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h2 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
            <Building className="w-8 h-8 text-blue-600" />
            إدارة الوظائف
          </h2>
          <p className="text-gray-600 mt-2">إدارة وعرض جميع الوظائف في النظام (الأحدث أولاً)</p>
        </div>
        <div className="text-sm text-gray-500">
          إجمالي الوظائف: <span className="font-bold text-blue-600">{jobs.length}</span>
        </div>
      </div>

      {jobs.length === 0 ? (
        <div className="text-center py-12">
          <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Building className="w-10 h-10 text-gray-400" />
          </div>
          <h3 className="text-xl font-semibold text-gray-600 mb-2">لا توجد وظائف</h3>
          <p className="text-gray-500">لم يتم إضافة أي وظائف بعد</p>
        </div>
      ) : (
        <div className="space-y-4">
          {jobs.map((job) => {
            const isExpired = isJobExpired(job.endDate);
            const isExpiringSoon = isJobExpiringSoon(job.endDate);
            
            return (
              <div
                key={job._id}
                className={`border rounded-2xl p-6 transition-all duration-300 hover:shadow-lg ${
                  isExpired 
                    ? 'bg-red-50 border-red-200' 
                    : isExpiringSoon
                    ? 'bg-orange-50 border-orange-200'
                    : 'bg-white border-gray-200'
                }`}
              >
                {editingJob?._id === job._id ? (
                  // Edit Mode
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">المسمى الوظيفي</label>
                        <input
                          name="title"
                          value={editingJob.title}
                          onChange={handleChange}
                          className="w-full border border-gray-300 rounded-xl p-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="المسمى الوظيفي"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">الشركة</label>
                        <input
                          name="company"
                          value={editingJob.company}
                          onChange={handleChange}
                          className="w-full border border-gray-300 rounded-xl p-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="اسم الشركة"
                        />
                      </div>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">الموقع</label>
                      <input
                        name="location"
                        value={editingJob.location}
                        onChange={handleChange}
                        className="w-full border border-gray-300 rounded-xl p-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="موقع العمل"
                      />
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">تاريخ البداية</label>
                        <input
                          type="date"
                          name="startDate"
                          value={editingJob.startDate}
                          onChange={handleChange}
                          className="w-full border border-gray-300 rounded-xl p-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">تاريخ الانتهاء</label>
                        <input
                          type="date"
                          name="endDate"
                          value={editingJob.endDate}
                          onChange={handleChange}
                          className="w-full border border-gray-300 rounded-xl p-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>
                    </div>

                    <div className="flex justify-end gap-3 pt-4">
                      <button
                        onClick={handleSave}
                        disabled={loading}
                        className="flex items-center gap-2 bg-green-600 text-white px-6 py-3 rounded-xl hover:bg-green-700 transition-colors duration-200 disabled:opacity-50"
                      >
                        <Save size={18} />
                        {loading ? 'جاري الحفظ...' : 'حفظ التغييرات'}
                      </button>
                      <button
                        onClick={handleCancelEdit}
                        className="flex items-center gap-2 bg-gray-500 text-white px-6 py-3 rounded-xl hover:bg-gray-600 transition-colors duration-200"
                      >
                        <X size={18} />
                        إلغاء
                      </button>
                    </div>
                  </div>
                ) : (
                  // View Mode
                  <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-start gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <h3 className="text-xl font-semibold text-gray-900">{job.title}</h3>
                            <div className={`px-3 py-1 rounded-full text-xs font-medium flex items-center gap-1 ${
                              isExpired 
                                ? 'bg-red-100 text-red-800' 
                                : isExpiringSoon
                                ? 'bg-orange-100 text-orange-800'
                                : 'bg-green-100 text-green-800'
                            }`}>
                              {isExpired ? (
                                <>
                                  <AlertCircle size={14} />
                                  منتهية
                                </>
                              ) : isExpiringSoon ? (
                                <>
                                  <Clock size={14} />
                                  تنتهي قريباً
                                </>
                              ) : (
                                <>
                                  <CheckCircle size={14} />
                                  نشطة
                                </>
                              )}
                            </div>
                          </div>
                          
                          <div className="flex flex-wrap gap-4 text-gray-600">
                            <div className="flex items-center gap-2">
                              <Building className="w-4 h-4" />
                              <span>{job.company}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <MapPin className="w-4 h-4" />
                              <span>{job.location}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <Calendar className="w-4 h-4" />
                              <span>ينتهي في {formatDate(job.endDate)}</span>
                            </div>
                            {/* عرض تاريخ الإنشاء إذا كان متوفراً */}
                            {job.createdAt && (
                              <div className="flex items-center gap-2 text-sm text-gray-500">
                                <span>أضيفت في: {getCreationDate(job)}</span>
                              </div>
                            )}
                          </div>
                          
                          {job.requirements && job.requirements.length > 0 && (
                            <div className="mt-3">
                              <div className="flex flex-wrap gap-2">
                                {job.requirements.slice(0, 3).map((req, index) => (
                                  <span 
                                    key={index}
                                    className="px-2 py-1 bg-blue-100 text-blue-800 rounded-lg text-xs"
                                  >
                                    {req}
                                  </span>
                                ))}
                                {job.requirements.length > 3 && (
                                  <span className="px-2 py-1 bg-gray-100 text-gray-600 rounded-lg text-xs">
                                    +{job.requirements.length - 3} أكثر
                                  </span>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-3 flex-wrap">
                      {/* زر تحميل Excel */}
                      <button
                        onClick={() => downloadExcel(job._id, job.title)}
                        disabled={downloading[job._id]}
                        className="flex items-center gap-2 bg-emerald-600 text-white px-4 py-2 rounded-xl hover:bg-emerald-700 transition-colors duration-200 disabled:opacity-50"
                      >
                        {downloading[job._id] ? (
                          <>
                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                            جاري...
                          </>
                        ) : (
                          <>
                            <Download size={16} />
                            Excel
                          </>
                        )}
                      </button>

                      {/* زر التعديل */}
                      <button
                        onClick={() => handleEdit(job)}
                        className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-xl hover:bg-blue-700 transition-colors duration-200"
                      >
                        <Pencil size={16} />
                        تعديل
                      </button>

                      {/* زر الحذف */}
                      <button
                        onClick={() => deleteJob(job._id, job.title)}
                        disabled={deleteLoading === job._id}
                        className="flex items-center gap-2 bg-red-600 text-white px-4 py-2 rounded-xl hover:bg-red-700 transition-colors duration-200 disabled:opacity-50"
                      >
                        {deleteLoading === job._id ? (
                          <>
                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                            جاري...
                          </>
                        ) : (
                          <>
                            <Trash2 size={16} />
                            حذف
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Stats Summary */}
      {jobs.length > 0 && (
        <div className="mt-8 pt-6 border-t border-gray-200">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="text-center p-4 bg-green-50 rounded-2xl border border-green-200">
              <div className="text-2xl font-bold text-green-600">
                {jobs.filter(job => !isJobExpired(job.endDate) && !isJobExpiringSoon(job.endDate)).length}
              </div>
              <div className="text-sm text-green-800">وظائف نشطة</div>
            </div>
            <div className="text-center p-4 bg-orange-50 rounded-2xl border border-orange-200">
              <div className="text-2xl font-bold text-orange-600">
                {jobs.filter(job => isJobExpiringSoon(job.endDate)).length}
              </div>
              <div className="text-sm text-orange-800">تنتهي قريباً</div>
            </div>
            <div className="text-center p-4 bg-red-50 rounded-2xl border border-red-200">
              <div className="text-2xl font-bold text-red-600">
                {jobs.filter(job => isJobExpired(job.endDate)).length}
              </div>
              <div className="text-sm text-red-800">منتهية</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageJobsTab;
